import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconAssetGroup } from 'svgIcons/MotionPortalIcons';
import { ScrollableList } from '../ScrollableListComponent';
import { LEFT_PANEL_WIDTH_PERCENT, MAX_CHAR_LENGTH, pageSize } from '../constants';
import {
    formatConditionIndexOverviewRequest,
    sortBasedOnOveralCondition,
    iconCondition
} from '../helpers';
import usePagination from 'sharedHooks/usePagination';
import { apiCallStatus } from 'helpers/constants';
import ExpandableComponent from './ExpandableComponent';
import TooltipTitleComponent from '../TooltipTitleComponent';
import { useAssetGroupSearchExpansion } from '../hooks/useAssetGroupSearchExpansion';
import { setAssetGroupSyncFlag } from '../../../GeneralFilter/GeneralFilterModal/actions';

const AssetOverviewComponent = (props) => {
    const { t: translate } = useTranslation();
    const [selectedAssetGroup, setSelectedAssetGroup] = useState(null);
    // const [selectedAsset, setSelectedAsset] = useState({});
    const [isHorizontalScrollActive, setIsHorizontalScrollActive] = useState(false);
    const [showAsset, setShowAsset] = useState(false);
    const [isLabelVisible, setIsLabelVisible] = useState(true);

    const itemsPerPage = 50; // Number of items to load per page
    const {
        pageNumber,
        assetGroupOverviewLoadingStatus,
        assetGroupOverviewList,
        hasMore,
        initialLoadDone,
        overviewAssetList,
        selectedAsset,
        refeshCIAMSync,
        refreshAssetGroups
    } = props.model;

    const { getAssetGroupConditionIndicesRequest, setSelectedAssetAction, previousSearchFilter } = props.actions;
    const {
        assetNumber,
        selectedFilters,
        searchBoxFilter,
        sensorIdentifierFilter,
        conditionFilter,
        assetTypeFilter,
        subscriptionTypeFilter,
        withVisibleFilter,
        allAssets,
        isFilterInProgress,
        leftPanelSize,
        sites
    } = props;
    const dispatch = useDispatch();
    const filteredSelectedFilters = useMemo(() => {
        if (!selectedFilters || !Array.isArray(selectedFilters?.countries) || selectedFilters?.countries?.length === 0 || !Array.isArray(sites) || sites?.length === 0) {
            return selectedFilters;
        }

        if (Array.isArray(selectedFilters?.sites) && selectedFilters.sites.length > 0) {
            return {
                ...selectedFilters,
                countries: [],
                sites: selectedFilters.sites,
                sitesChecked: true
            };
        }

        const getCountryCodeFromCountry = (country) => country?.countryCode ?? country?.code ?? country;

        const selectedCountryCodes = (selectedFilters?.countries || [])
            .map(getCountryCodeFromCountry)
            .filter(Boolean);

        if (selectedCountryCodes?.length === 0) { return selectedFilters; }

        const getSiteCountryCode = (site) => site?.countryCode ?? site?.country?.countryCode ?? site?.country ?? site?.country_code;
        const getSiteId = (site) => site?.siteID ?? site?.siteId ?? site?.SiteId ?? site?.id;

        const matchingSites = (sites || []).filter(site => {
            const siteCountry = getSiteCountryCode(site);
            return siteCountry && selectedCountryCodes.includes(siteCountry);
        }).map(site => {
            const sid = getSiteId(site);
            return { ...site, siteID: sid };
        }).filter(s => s.siteID !== null);

        return {
            ...selectedFilters,
            countries: [],
            sites: matchingSites,
            sitesChecked: matchingSites?.length > 0
        };
    }, [selectedFilters, sites]);
    const memoizedAssetGroupOverviewList = useMemo(() => assetGroupOverviewList, [assetGroupOverviewList]);
    const memoizedFilters = useMemo(() => ({
        selectedFilters: filteredSelectedFilters,
        conditionFilter,
        assetTypeFilter,
        subscriptionTypeFilter,
        searchBoxFilter,
        sensorIdentifierFilter
    }), [filteredSelectedFilters, conditionFilter, assetTypeFilter, subscriptionTypeFilter, searchBoxFilter, sensorIdentifierFilter]);
    const { paginatedItems, loadMoreItems } = usePagination(memoizedAssetGroupOverviewList, itemsPerPage);
    const timerRef = useRef(null);
    const sortedAssetGroups = sortBasedOnOveralCondition(paginatedItems);

    useEffect(() => {
        if (assetGroupOverviewLoadingStatus === apiCallStatus.LOADING ||
            !(isFilterInProgress || !initialLoadDone || refeshCIAMSync || refreshAssetGroups)) {
            return undefined;
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            getAssetGroupConditionIndicesRequest(formatConditionIndexOverviewRequest(
                1,
                memoizedFilters.selectedFilters,
                memoizedFilters.searchBoxFilter,
                memoizedFilters.conditionFilter,
                memoizedFilters.assetTypeFilter,
                memoizedFilters.subscriptionTypeFilter,
                memoizedFilters.sensorIdentifierFilter,
                refreshAssetGroups
            ));
            dispatch(setAssetGroupSyncFlag());
            previousSearchFilter(memoizedFilters.searchBoxFilter);
        }, memoizedFilters.searchBoxFilter?.length > 0 ? 700 : 0);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [
        memoizedFilters,
        isFilterInProgress,
        initialLoadDone,
        assetGroupOverviewLoadingStatus,
        refeshCIAMSync,
        refreshAssetGroups,
        getAssetGroupConditionIndicesRequest,
        previousSearchFilter,
        dispatch
    ]);

    const handleSelectedItem = (asset) => {
        setSelectedAssetAction(asset, overviewAssetList);
    };

    const handleReset = () => {
        setSelectedAssetAction(null);
    };

    const handleAssetToggle = useCallback(() => setShowAsset(prevShowAsset => !prevShowAsset), []);

    const handleAssetGroupClick = (assetGroup, index, toggleSize) => {
        if (selectedAssetGroup === assetGroup?.assetGroupId) {
            setSelectedAssetGroup(null);
        } else {
            setSelectedAssetGroup(assetGroup?.assetGroupId);
        }
        const assetCount = assetGroup?.assets?.length + 1 || 1; // Default to 1 if no assets
        toggleSize(index, assetCount);
    };

    // Add search expansion functionality for asset groups
    useAssetGroupSearchExpansion(
        searchBoxFilter,
        memoizedAssetGroupOverviewList,
        selectedAssetGroup,
        setSelectedAssetGroup,
        handleSelectedItem,
        selectedAsset,
        sensorIdentifierFilter,
        assetGroupOverviewLoadingStatus
    );

    const onHorizontalScrollHandler = (isScroll) => {
        setIsHorizontalScrollActive(isScroll);
    };

    const onLabelVisibilityHandler = (isVisibe) => {
        setIsLabelVisible(isVisibe);
    };


    const calculateTotalAssetsByCondition = (assetGroup, condition) => {
        return assetGroup?.assets?.filter((asset) => asset?.condition === condition)?.length || 0;
    };

    const onLoadMore = () => {
        if (paginatedItems?.length < memoizedAssetGroupOverviewList?.length) {
            loadMoreItems();
        } else if (hasMore) {
            getAssetGroupConditionIndicesRequest(formatConditionIndexOverviewRequest(
                pageNumber,
                memoizedFilters.selectedFilters,
                memoizedFilters.searchBoxFilter,
                memoizedFilters.conditionFilter,
                memoizedFilters.assetTypeFilter,
                memoizedFilters.sensorIdentifierFilter
            ));
        }
    };

    const renderExpandableTitle = useCallback((item, index) => {
        const poorAssets = calculateTotalAssetsByCondition(item, 'Poor');
        const tolerableAssets = calculateTotalAssetsByCondition(item, 'Tolerable');
        const unknownAssets = calculateTotalAssetsByCondition(item, 'Unknown');
        const isLengthyTitle = item?.assetGroupName?.length > MAX_CHAR_LENGTH;
        return <>
            <div className={`item-name powertrain__cell-fixed ${isHorizontalScrollActive ? 'horizontal-scroll-active' : ''}`}>
                <div className='asset-condition-icon'>
                    <span className={`asset-condition ${iconCondition(item?.condition)}`}></span>
                </div>
                <div className='item-icon'><IconAssetGroup /></div>
                <div className={`name d-flex ${isLengthyTitle ? '' : 'text-truncate'}`}>
                    {isLengthyTitle ?
                        <TooltipTitleComponent index={index} title={item?.assetGroupName} leftPanelSize={leftPanelSize} count={item?.assets?.length} />
                        : <><div className='text-truncate'>{item?.assetGroupName}</div>&nbsp;{`(${item?.assets?.length})`}</>}
                </div>
                {leftPanelSize >= LEFT_PANEL_WIDTH_PERCENT && <>
                    <div className='conditions-item'>{`${translate('ABB.Powertrain.Frontend.poorLabel')}: ${poorAssets}`}</div>
                    <div className='conditions-item'>{`${translate('ABB.Powertrain.Frontend.tolerableLabel')}: ${tolerableAssets}`}</div>
                    <div className='conditions-item'>{`${translate('ABB.Powertrain.Frontend.unknownLabel')}: ${unknownAssets}`}</div>
                </>}
            </div>
        </>;
    }, [leftPanelSize]);

    const renderAssetGroups = ({ item, index, toggleSize }) => {
        return <ExpandableComponent
            key={item?.assetGroupId}
            item={item}
            index={index}
            renderExpandableTitle={() => renderExpandableTitle(item, index)}
            handlePowertrainClick={() => handleAssetGroupClick(item, index, toggleSize)}
            selectedPowertrain={selectedAssetGroup}
            selectedAsset={selectedAsset}
            handleSelectedItem={handleSelectedItem}
            handleAssetToggle={handleAssetToggle}
            isHorizontalScrollActive={isHorizontalScrollActive}
            assetGroupList={memoizedAssetGroupOverviewList}
            translate={translate}
            model={props.model}
            allAssets={allAssets}
            isLabelVisible={isLabelVisible}
            leftPanelSize={leftPanelSize}
        />;
    };

    return <ScrollableList
        role='scrollable-list'
        noDataLabel={translate('ABB.Powertrain.Frontend.conditionIndicesOverviewNoItems')}
        className={`asset overview ${withVisibleFilter ? 'with-filter' : ''}`}
        assetNumber={assetNumber}
        loadingStatus={assetGroupOverviewLoadingStatus}
        hasMore={true}
        showThreshold={props?.model?.showThreshold}
        showTrend={props?.model?.showTrend}
        pageSize={pageSize}
        loadMore={onLoadMore}
        renderList={renderAssetGroups}
        selectedAsset={selectedAsset}
        hasData={sortedAssetGroups}
        selectedAssetGroup={selectedAssetGroup}
        onReset={handleReset}
        showAsset={showAsset}
        onHorizontalScroll={onHorizontalScrollHandler}
        onLabelVisibilityChange={onLabelVisibilityHandler}
        leftPanelSize={leftPanelSize}
        noWrapper={false}
        listType='VariableSizeList'
    />;
};

AssetOverviewComponent.propTypes = {
    model: PropTypes.shape({
        pageNumber: PropTypes.number,
        assetOverviewLoadingStatus: PropTypes.string,
        assetOverviewList: PropTypes.array,
        hasMore: PropTypes.bool,
        initialLoadDone: PropTypes.bool,
        showTrend: PropTypes.bool,
        showThreshold: PropTypes.bool,
        assetGroupOverviewLoadingStatus: PropTypes.string,
        assetGroupOverviewList: PropTypes.array,
        refreshAssetGroups: PropTypes.bool,
        refeshCIAMSync: PropTypes.bool,
    }),
    actions: PropTypes.shape({
        getAssetGroupConditionIndicesRequest: PropTypes.func,
        setSelectedAssetAction: PropTypes.func,
        previousSearchFilter: PropTypes.func
    }),
    assetGroupOverviewLoadingStatus: PropTypes.string,
    assetGroupOverviewList: PropTypes.array,
    assetNumber: PropTypes.number,
    selectedFilters: PropTypes.object,
    searchBoxFilter: PropTypes.string,
    sensorIdentifierFilter: PropTypes.string,
    conditionFilter: PropTypes.array,
    assetTypeFilter: PropTypes.string,
    withVisibleFilter: PropTypes.bool,
    isFilterInProgress: PropTypes.bool,
    subscriptionTypeFilter: PropTypes.array,
    allAssets: PropTypes.array,
    showThreshold: PropTypes.bool,
    showTrend: PropTypes.bool,
    assetGroup: PropTypes.shape({
        assetGroupId: PropTypes.number,
        selectedAsset: PropTypes.object,
        assetGroupName: PropTypes.string,
        assets: PropTypes.array,
    }),
};


export default AssetOverviewComponent;
